/**
 * Which fiscal action an archive row offers - §9.645, owner O36.
 *
 * **The owner**: *"In zoznam & archív - when storno fiškálny doklad is not available, add buttom for «Print
 * fiškálny doklad», and when its printed, make sure that «Storno fiškálny doklad» will be there. So when they do
 * objednávka «Doručená» they still can do fiškálny doklad"*.
 *
 * **Written as complements rather than as two rules.** Storno is for a **live** document and print is for the
 * absence of one, so they cannot both be hidden - which is the defect the owner is reporting - and cannot both be
 * shown, which would be a till offering to void and reissue the same receipt at once. Two independent conditions
 * would allow either state; one predicate and its negation cannot.
 *
 * **Extracted rather than left inline**, because the pair is the behaviour: a component can be read to see which
 * buttons exist, and only a rule can be asked whether they are mutually exclusive on every input.
 */

/** Just enough of an order row to decide. Narrow on purpose, so a test needs no fixture of the whole list item. */
export interface FiskalRowFacts {
  status?: string | null
  fiskalPrintedAt?: string | null
  fiskalStornoAt?: string | null
}

export interface FiskalRowActions {
  showStorno: boolean
  showPrint: boolean
}

/**
 * Whether this order currently has a fiscal document that stands.
 *
 * **A stornoed receipt counts as absent**, and the product already expects that sequence: §10A.191 is
 * `AReissuedReceiptCanBeStornoedAgainTest`, so reissuing after a void is supported server-side rather than
 * being an edge case this screen invents.
 */
export function hasLiveFiskalDocument(order: FiskalRowFacts): boolean {
  return Boolean(order.fiskalPrintedAt) && !order.fiskalStornoAt
}

/**
 * **A cancelled order is never offered a receipt.** It is not a sale, and a fiscal document for one is a tax
 * record of something that did not happen. `SETTLING_STATUSES` names `cancelled` beside `delivered` and
 * `quality_check`, so the word is the product's own rather than a literal invented here.
 *
 * Every other status may be printed, including an open one: the till issues a document mid-service, and a list
 * that refused to would be answering a different question from the one the owner asked.
 */
export function fiskalRowActions(order: FiskalRowFacts, canFiskal: boolean): FiskalRowActions {
  if (!canFiskal) return { showStorno: false, showPrint: false }
  const live = hasLiveFiskalDocument(order)
  return { showStorno: live, showPrint: !live && order.status !== 'cancelled' }
}
